import React, { useState, useEffect } from "react";
import AOS from "aos";
import "./Contact.css";

export default function Contact() {
  const [formData, setFormData] = useState({
    clientName: "",
    whatsapp: "",
    serviceType: "",
    attachmentPath: null,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    AOS.init({ duration: 800, offset: 100 });
  }, []);

  const allowedFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/zip",
    "image/png",
    "image/jpeg",
    "image/jpg",
  ];

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      if (!allowedFileTypes.includes(file.type)) {
        setStatus(
          "Invalid file type. Only PDF, DOCX, ZIP, or images are allowed.",
        );
        return;
      } else {
        setStatus("");
      }
      setFormData({ ...formData, [name]: file });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    try {
      const data = new FormData();
      data.append("ClientName", formData.clientName);
      data.append("Whatsapp", formData.whatsapp);
      data.append("ServiceType", formData.serviceType);
      data.append("Message", formData.message);
      if (formData.attachmentPath)
        data.append("AttachmentPath", formData.attachmentPath);

      const res = await fetch(
        `https://api.mzrsoftware.com/api/SendEmail/send`,
        {
          method: "POST",
          body: data,
        },
      );

      if (res.ok) {
        setStatus("✅ Message sent successfully! We'll reach out soon.");
        setFormData({
          clientName: "",
          whatsapp: "",
          serviceType: "",
          attachmentPath: null,
          message: "",
        });
      } else {
        const error = await res.json();
        setStatus(
          error?.message || "Failed to send message. Please try again.",
        );
      }
    } catch (error) {
      console.error(error);
      setStatus("Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  const services = [
    "Custom Software Development",
    "POS & ERP Systems",
    "Web & E-Commerce",
    "Cloud & DevOps",
    "Mobile Development",
    "AI & Machine Learning",
  ];

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5" data-aos="fade-up">
          <h2 className="section-title">
            Let's <span className="highlight">Connect</span> & Build Together
          </h2>
          <p className="section-subtitle">
            Have a project in mind? We'd love to discuss it with you.
          </p>
        </div>

        <div className="row g-5">
          {/* Contact Info */}
          <div className="col-lg-5" data-aos="fade-right">
            <div className="contact-info-box">
              <h3 className="info-title">Get In Touch</h3>
              <p className="info-description">
                Reach out through any of these channels. Our team responds
                within 24 hours.
              </p>

              <div className="info-items">
                <div
                  className="info-item"
                  data-aos="fade-up"
                  data-aos-delay="50"
                >
                  <div className="icon-box">📧</div>
                  <div>
                    <h4>Email</h4>
                    <a href="mailto:info@mzrsoftware.com">
                      info@mzrsoftware.com
                    </a>
                  </div>
                </div>

                <div
                  className="info-item"
                  data-aos="fade-up"
                  data-aos-delay="100"
                >
                  <div className="icon-box">💬</div>
                  <div>
                    <h4>WhatsApp</h4>
                    <a
                      href="https://wa.me/923351206586"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      +92 335 120 6586
                    </a>
                  </div>
                </div>

                <div
                  className="info-item"
                  data-aos="fade-up"
                  data-aos-delay="150"
                >
                  <div className="icon-box">🌍</div>
                  <div>
                    <h4>Location</h4>
                    <p>Pakistan | Available Globally</p>
                  </div>
                </div>
              </div>

              <div
                className="expertise-box"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                <h4>Our Expertise</h4>
                <div className="expertise-tags">
                  {services.map((service, index) => (
                    <span key={index} className="expertise-tag">
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="col-lg-7" data-aos="fade-left">
            <div className="contact-form-box">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="clientName" className="form-label">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="clientName"
                    className="form-input"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleChange}
                    placeholder="Full Name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="whatsapp" className="form-label">
                    WhatsApp Number *
                  </label>
                  <input
                    type="tel"
                    id="whatsapp"
                    className="form-input"
                    name="whatsapp"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="+92 335 120 6586"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="serviceType" className="form-label">
                    Service Type *
                  </label>
                  <select
                    id="serviceType"
                    className="form-input form-select"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service...</option>
                    <option value="custom-software">
                      Custom Software Development
                    </option>
                    <option value="pos-erp">POS & ERP Systems</option>
                    <option value="web-ecommerce">Web & E-Commerce</option>
                    <option value="cloud-devops">
                      Cloud & DevOps Solutions
                    </option>
                    <option value="mobile">Mobile Development</option>
                    <option value="ai">AI & Machine Learning</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="attachmentPath" className="form-label">
                    Requirements (Optional)
                  </label>
                  <input
                    type="file"
                    id="attachmentPath"
                    className="form-input"
                    name="attachmentPath"
                    accept=".pdf,.docx,.zip,image/*"
                    onChange={handleChange}
                  />
                  <small className="form-hint">
                    PDF, DOCX, ZIP, PNG, JPG - Max 10MB
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Project Details *
                  </label>
                  <textarea
                    id="message"
                    className="form-input form-textarea"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, requirements, timeline, and budget..."
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Message →"}
                </button>

                {status && (
                  <div
                    className={`status-message ${
                      status.includes("successfully") ? "success" : "error"
                    }`}
                  >
                    {status}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
